// Connection-health monitor — the feature's brain (BL-1090).
//
// Fuses three inputs into the two booleans the shared bar renders:
//
//   HINTS (never a verdict)  navigator online/offline events + the initial
//                            `navigator.onLine` reading, and the realtime
//                            transport reporting a channel drop.
//   VERDICT                  the reachability probe. Only this can assert
//                            offline, and only after the detector's
//                            consecutive-failure threshold.
//   PROOF OF LIFE            the transport reporting `reconnected` — a channel
//                            that just re-subscribed is stronger evidence the
//                            network works than any probe, so it clears
//                            immediately without waiting out the backoff.
//
// A plain controller rather than a hook so all the timing is testable in node —
// the same store + `useSyncExternalStore` split the realtime LotStateStore
// already uses. `ConnectionHealthProvider` is the thin React binding.
//
// Two properties worth stating because their absence caused BUG-1126:
//   1. It NEVER polls while healthy. Probes run only while a hint is
//      outstanding or while offline; a healthy tab costs zero requests.
//   2. The recovery loop is channel-independent. The removed implementation
//      gated its probe on `activeChannels > 0` against an emitter with zero
//      producers, so the loop never ran at all.

import type { RealtimeSubscriber } from '../../types/realtime/subscriber';
import type { Unsubscribe } from '../../types/realtime/types';
import { createOfflineDetector, type OfflineDetector } from './offline-detector';
import type { ProbeOutcome } from './reachability-probe';

/**
 * The browser surface the monitor needs. Injected rather than read off
 * `globalThis` so the timing behaviour is testable without a DOM.
 */
export interface ConnectionEnvironment {
    /** The browser's current (unreliable) online flag — a hint only. */
    isOnLine(): boolean;
    /**
     * Whether the page is in the foreground. Optional: an environment without
     * it is treated as always visible, so the feed grace window simply never
     * restarts on a tab return.
     */
    isVisible?(): boolean;
    addEventListener(event: ConnectionEnvironmentEvent, listener: () => void): void;
    removeEventListener(event: ConnectionEnvironmentEvent, listener: () => void): void;
}

/** The environment events the monitor listens to. */
export type ConnectionEnvironmentEvent = 'online' | 'offline' | 'visibilitychange';

/**
 * How long a realtime feed drop must persist before "Reconnecting" shows
 * (BUG-2016). socket.io's own jittered reconnect lands well inside it, and so
 * does the hourly serverless-NEG drop every connection takes (ADR 046 —
 * Realtime Transport § 7), so neither flashes the bar. A drop that outlasts it
 * is a real feed loss, and the bar says so.
 */
export const DEFAULT_FEED_GRACE_MS = 10_000;

/** Timer surface; defaults to `globalThis`. Tests pass fake-timer-friendly stubs. */
export interface MonitorTimers {
    setTimeout(handler: () => void, ms: number): unknown;
    clearTimeout(handle: unknown): void;
}

/** What the bar renders. */
export interface ConnectionHealthState {
    /** Probe-corroborated: the network is genuinely unreachable. */
    isOffline: boolean;
    /** The realtime channel dropped after having joined. */
    isFeedDisconnected: boolean;
}

export interface ConnectionHealthMonitorOptions {
    /** Transport whose connection events drive feed health. */
    subscriber: RealtimeSubscriber;
    /** Runs one reachability probe. Injected so tests need no network. */
    probe: () => Promise<ProbeOutcome>;
    /** Browser surface. Defaults to a `globalThis.window`-backed adapter. */
    environment?: ConnectionEnvironment;
    /** Timer surface. Defaults to `globalThis`. */
    timers?: MonitorTimers;
    /** Override the hysteresis / backoff policy. */
    detector?: OfflineDetector;
    /** Override {@link DEFAULT_FEED_GRACE_MS}. */
    feedGraceMs?: number;
}

export interface ConnectionHealthMonitor {
    /** Current state. Referentially stable until it actually changes. */
    getState(): ConnectionHealthState;
    /** Subscribe to state transitions. Returns an idempotent unsubscribe. */
    subscribe(listener: () => void): Unsubscribe;
    /**
     * Install listeners and begin reacting.
     *
     * @returns A disposer that removes every listener and stops the probe loop.
     * @sideEffect Registers online/offline + transport listeners and may issue
     *   network probes.
     */
    start(): Unsubscribe;
    /**
     * Ask for one reachability probe now (BL-2342). For callers that know
     * something the monitor can't see: the admin phone app calls it on an
     * offline cold start (the socket never connected, so no 'disconnected'
     * fires, and Android keeps `navigator.onLine` true in airplane mode), on a
     * return to the foreground, and when leaving manual offline mode.
     *
     * It is a hint like any other: one probe, and the existing backoff loop
     * only if that probe fails. A no-op before `start()` and after disposal.
     *
     * @sideEffect May issue a network probe.
     */
    recheck(): void;
}

const HEALTHY: ConnectionHealthState = {
    isOffline: false,
    isFeedDisconnected: false,
};

/**
 * Create a connection-health monitor.
 *
 * @param options - {@link ConnectionHealthMonitorOptions}
 * @returns An unstarted {@link ConnectionHealthMonitor}; call `start()` to arm it.
 */
export function createConnectionHealthMonitor(
    options: ConnectionHealthMonitorOptions,
): ConnectionHealthMonitor {
    const detector = options.detector ?? createOfflineDetector();
    const timers = options.timers ?? getGlobalTimers();
    const environment = options.environment ?? getGlobalEnvironment();
    const feedGraceMs = options.feedGraceMs ?? DEFAULT_FEED_GRACE_MS;

    const listeners = new Set<() => void>();
    let state: ConnectionHealthState = HEALTHY;
    // `feedDown` is the transport's truth; `feedDisconnected` is what the bar
    // shows, and only after `feedDown` has outlasted the grace window.
    let feedDown = false;
    let feedDisconnected = false;
    let probeInFlight = false;
    let pendingHandle: unknown | undefined;
    let graceHandle: unknown | undefined;
    let stopped = true;

    const isVisible = () => environment.isVisible?.() ?? true;

    /** Recompute the exposed state, keeping identity stable when nothing moved. */
    function publish() {
        const next: ConnectionHealthState = {
            isOffline: detector.isOffline(),
            isFeedDisconnected: feedDisconnected,
        };
        if (
            next.isOffline === state.isOffline &&
            next.isFeedDisconnected === state.isFeedDisconnected
        ) {
            return;
        }
        state = next;
        listeners.forEach((listener) => listener());
    }

    function cancelPending() {
        if (pendingHandle !== undefined) {
            timers.clearTimeout(pendingHandle);
            pendingHandle = undefined;
        }
    }

    /**
     * Run one probe, then decide whether to keep looping. The loop continues
     * only while probes keep failing — one success ends it, so a healthy tab
     * never carries a background poll.
     */
    async function runProbe(): Promise<void> {
        if (stopped || probeInFlight) return;
        probeInFlight = true;

        let outcome: ProbeOutcome;
        try {
            outcome = await options.probe();
        } catch (err) {
            // `runReachabilityProbe` never throws — it catches internally — so
            // reaching here means an injected probe misbehaved. Recording it as
            // a failure is honest (no answer came back) and keeps the loop
            // alive rather than letting the rejection escape an un-awaited
            // call, but it must not be silent: a probe that throws on EVERY
            // call would pin the UI offline forever with no clue why. Passing
            // `err` as the trailing arg lets the console patch promote
            // error.message / .stack (per structured-logging.md).
            console.error(
                '[connection-health] probe threw instead of resolving; treating as unreachable',
                err,
            );
            outcome = 'unreachable';
        } finally {
            probeInFlight = false;
        }

        if (stopped) return;

        detector.record(outcome);
        publish();

        if (outcome === 'unreachable') {
            scheduleProbe(detector.nextDelayMs());
        }
    }

    function scheduleProbe(delayMs: number) {
        if (stopped) return;
        cancelPending();
        pendingHandle = timers.setTimeout(() => {
            pendingHandle = undefined;
            void runProbe();
        }, delayMs);
    }

    /** A hint arrived — corroborate it immediately rather than believing it. */
    function kickProbe() {
        if (stopped) return;
        cancelPending();
        void runProbe();
    }

    function cancelGrace() {
        if (graceHandle !== undefined) {
            timers.clearTimeout(graceHandle);
            graceHandle = undefined;
        }
    }

    /**
     * (Re)start the grace window. When it elapses with the feed still down and
     * the tab in view, the bar shows. A timer that fires while the tab is
     * hidden does nothing: the return to the foreground restarts the window, so
     * a stale tab never greets the operator with a drop socket.io is about to
     * heal (BUG-2016).
     */
    function armGrace() {
        if (stopped) return;
        cancelGrace();
        graceHandle = timers.setTimeout(() => {
            graceHandle = undefined;
            if (stopped || !feedDown || !isVisible()) return;
            feedDisconnected = true;
            publish();
        }, feedGraceMs);
    }

    function onConnectionEvent(event: 'disconnected' | 'reconnected') {
        if (event === 'disconnected') {
            feedDown = true;
            armGrace();
            // A dropped channel may be the ONLY hint available: on the VPN /
            // interface-state setups that motivated this rebuild,
            // `navigator.onLine` never flips. Probe at once, not after the
            // grace window, so a dead network is reported as offline rather
            // than as merely delayed.
            kickProbe();
            return;
        }
        // 'reconnected' — the transport is demonstrably alive.
        feedDown = false;
        feedDisconnected = false;
        cancelGrace();
        detector.reset();
        cancelPending();
        publish();
    }

    /**
     * Back in the foreground with the feed still down and the bar not yet
     * shown: give it a fresh window. A bar that was already showing before the
     * tab was hidden stays put, so a quick tab switch during a real feed loss
     * doesn't hide it for another grace period.
     */
    function onVisibilityChange() {
        if (!isVisible() || !feedDown || feedDisconnected) return;
        armGrace();
    }

    return {
        getState: () => state,

        recheck: () => kickProbe(),

        subscribe(listener) {
            listeners.add(listener);
            let disposed = false;
            return () => {
                if (disposed) return;
                disposed = true;
                listeners.delete(listener);
            };
        },

        start() {
            stopped = false;

            const onOffline = () => kickProbe();
            // An `online` event is treated exactly like an `offline` one: a
            // hint worth corroborating, never a verdict. Recovery does not
            // depend on it firing — the backoff loop clears the state on its
            // own — but when it does fire it shortcuts the wait.
            const onOnline = () => kickProbe();

            environment.addEventListener('offline', onOffline);
            environment.addEventListener('online', onOnline);
            environment.addEventListener('visibilitychange', onVisibilityChange);

            const offConnectionChange =
                options.subscriber.onConnectionChange?.(onConnectionEvent) ??
                (() => {});

            // Reconcile the initial flag after arming, not in a constructor:
            // browsers can report a stale `false` before the network stack is
            // up, and no `online` event follows. Treated as a hint, so the
            // probe settles it either way.
            if (!environment.isOnLine()) kickProbe();

            let disposed = false;
            return () => {
                if (disposed) return;
                disposed = true;
                stopped = true;
                cancelPending();
                cancelGrace();
                environment.removeEventListener('offline', onOffline);
                environment.removeEventListener('online', onOnline);
                environment.removeEventListener('visibilitychange', onVisibilityChange);
                offConnectionChange();
            };
        },
    };
}

function getGlobalTimers(): MonitorTimers {
    return {
        setTimeout: (handler, ms) =>
            globalThis.setTimeout(handler, ms) as unknown,
        clearTimeout: (handle) =>
            globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
    };
}

/**
 * Browser-backed environment. In SSR (no `window`/`navigator`) it reports
 * online and visible and installs nothing, so the monitor stays inert on the
 * server. `visibilitychange` is dispatched at `document`, so it listens there.
 */
function getGlobalEnvironment(): ConnectionEnvironment {
    const hasWindow = typeof window !== 'undefined';
    const hasDocument = typeof document !== 'undefined';
    const targetFor = (event: ConnectionEnvironmentEvent): EventTarget | null => {
        if (event === 'visibilitychange') return hasDocument ? document : null;
        return hasWindow ? window : null;
    };
    return {
        isOnLine: () =>
            typeof navigator === 'undefined' ? true : navigator.onLine,
        isVisible: () =>
            hasDocument ? document.visibilityState !== 'hidden' : true,
        addEventListener: (event, listener) => {
            targetFor(event)?.addEventListener(event, listener);
        },
        removeEventListener: (event, listener) => {
            targetFor(event)?.removeEventListener(event, listener);
        },
    };
}
